import {DestroyRef, Injectable} from '@angular/core';
import {AppConfigService} from '../../common/service/app-config.service';
import {MetaData, NgEventBus} from 'ng-event-bus';

import {SpatialUtilService} from './spatial-util.service';
import {WidgetService} from './widget.service';
import {MapToolsService} from './map-tools.service';
import {DisplayPreferencesService} from '../../common/service/display-preferences.service';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {MapMode} from '../../common/models/map-mode.model';

export const DEFAULT_MAP_MODE: MapMode = {id: 0, type: 'mainPlan', label: 'Main Plan'};
/* eslint-disable @typescript-eslint/no-explicit-any */
@Injectable({
    providedIn: 'root'
})
export class MapModeService {
    private title = 'MapModeService';
    mapModeList: MapMode[] = [];
    mapMode: MapMode = DEFAULT_MAP_MODE;
    viewMode: string;
    viewModeResolution: string;

    constructor(
        private destroyRef: DestroyRef,
        private displayPreferencesService: DisplayPreferencesService,
        private spatialUtilService: SpatialUtilService,
        private widgetService: WidgetService,
        private mapToolsService: MapToolsService,
        private appConfigService: AppConfigService,
        private eventBus: NgEventBus
    ) {
        this.eventBus
            .on('changeMapMode')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((meta: MetaData) => {
                console.debug(this.title + ' : changeMapMode by watch');
                const indexToSet = this.mapModeList.findIndex(item => item.id === meta.data);
                if (indexToSet > -1) {
                    this.mapMode = this.mapModeList[indexToSet];
                }
                this.mapModeChange();
            });
        this.eventBus
            .on('addEnlargementOnMapModeList')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((meta: MetaData) => {
                console.debug(this.title + '  : addEnlargementOnMapModeList. ', meta.data);
                this.addEnlargementsToMapModeList(meta.data);
            });

        /**
         * DZ Removed 'removeEnlargementOnMapModeList' related code from this service to to EnlargementDeleteService where it belongs
         * PAU-457 Upgrade app\components\design\widgets\enlargement\services\enlargement-delete-service.js
         */
        // TODO PAU-461
        this.eventBus
            .on('updateEnlargement')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((meta: MetaData) => {
                console.debug(this.title + '  : updateEnlargement. ', meta.data.enlargement, meta.data.previousName);
                this.mapModeList.forEach(mapMode => {
                    if (mapMode.label === meta.data.originalName) {
                        mapMode.label = meta.data.enlargement.attributes.name;
                        mapMode.enlargementFeature = meta.data.enlargement;
                    }
                });
            });
    }

    init() {
        this.mapMode = {id: 0, type: 'mainPlan', label: 'Main Plan'};
        this.mapModeList.splice(0, this.mapModeList.length, this.mapMode);

        this.viewMode = this.appConfigService.getDesignProperties().VIEW_MODES.BLACK_BACKGROUND_MODE;
        this.viewModeResolution =
            this.displayPreferencesService.viewModeResolution || this.appConfigService.getDesignProperties().VIEW_MODE_RESOLUTIONS[0].value;
        this.eventBus.cast('mapModeInit');
    }

    mapModeChange(options: any = {}) {
        console.debug(this.title + ' : mapModeChange');

        const zoomPlanExtent = options.zoomPlanExtent !== false;

        this.widgetService.setWidgetDisabled('ConstructionTools', false);

        if (zoomPlanExtent) {
            this.eventBus.cast('zoomPlanExtent');
        }

        this.eventBus.cast('setMapMode', true);

        this.eventBus.cast('closeIdentifiedFeatures');

        this.eventBus.cast('closeEnlargementWidget');

        this.eventBus.cast('mapModeChanged');
    }

    updateMapMode() {
        this.displayPreferencesService.displayPreferences.showEnlargementFeatures =
            this.displayPreferencesService.displayPreferences.showEnlargementFeatures && !this.isPrintPreviewForEnlargement();
        this.displayPreferencesService.displayPreferences.showClippingFeatures =
            this.displayPreferencesService.displayPreferences.showClippingFeatures || this.isPrintPreviewForEnlargement();
        this.displayPreferencesService.displayPreferences.showPrintArea =
            this.displayPreferencesService.displayPreferences.showPrintArea && this.mapMode.type === 'mainPlan' && !this.isPrintPreview();
        this.eventBus.cast('setMapMode', false);
    }

    isMapModeType(type) {
        return type === this.mapMode.type;
    }

    isMapModeId(id) {
        return id === this.mapMode.id;
    }

    addEnlargementsToMapModeList(enlargement) {
        console.debug(this.title + ' : addEnlargementsToMapModeList');

        if (enlargement) {
            this.mapModeList.push({
                id: enlargement.attributes.id,
                type: 'enlargement',
                label: enlargement.attributes.name,
                enlargementFeature: enlargement
            });
        } else {
            this.mapModeList.splice(0, this.mapModeList.length, this.mapModeList[0]);

            const enlargements = this.spatialUtilService.getEnlargements();

            this.mapModeList.push(
                ...enlargements.map(enlargement => ({
                    id: enlargement.attributes.id,
                    type: 'enlargement',
                    label: enlargement.attributes.name,
                    enlargementFeature: enlargement
                }))
            );
            this.mapModeList = this.mapModeList.sort((a, b) => a.id - b.id);

            if (!this.mapModeList.some(v => this.mapMode && this.mapMode.id === v.id && this.mapMode.type === v.type)) {
                //Map mode either isn't set or is set to an enlargement that no longer exists, so reset to main plan.
                this.mapMode = this.mapModeList[0];
            }
        }
        this.mapModeChange();
    }

    updateEnlargementScaleInList(enlargementFeature) {
        console.debug(this.title + ' : updateEnlargementScaleInList');
        this.mapModeList.forEach(item => {
            if (item.id === enlargementFeature.attributes.id) {
                if (item.enlargementFeature && item.enlargementFeature.attributes) {
                    item.enlargementFeature.attributes.scale = enlargementFeature.attributes.scale;
                }
            }
        });
    }

    viewModeChanged(newViewMode, newViewModeResolution) {
        console.debug(this.title + ' : viewModeChanged');

        this.viewMode = newViewMode;
        this.viewModeResolution = newViewModeResolution;
        this.displayPreferencesService.viewMode = this.viewMode;
        this.displayPreferencesService.viewModeResolution = this.viewModeResolution;

        this.eventBus.cast('switchCadastre', this.mapToolsService.isActive('SHOW_HIDE_CADASTRE') && !this.isPrintPreview());
        this.updateMapMode();
    }

    isPrintPreviewForEnlargement() {
        return this.isPrintPreview() && !this.isMapModeType('mainPlan');
    }

    isPrintPreview() {
        return this.viewMode === this.appConfigService.getDesignProperties().VIEW_MODES.PRINT_PREVIEW_MODE;
    }
}
